import { useState } from "react";

import { clamp } from "lodash-es";
import { useLocalStorage } from "usehooks-ts";

import type { FloatingWindowGeometry } from "src/base-components/FloatingWindow/FloatingWindow";

const STORAGE_KEY = "unifiedCopilotWindowGeometry";

/** How much of the window has to stay on screen for it to be reachable. */
const MIN_VISIBLE_PX = 80;

const isGeometry = (value: unknown): value is FloatingWindowGeometry =>
  typeof value === "object" &&
  value !== null &&
  ["x", "y", "width", "height"].every((key) =>
    Number.isFinite((value as Record<string, unknown>)[key]),
  );

/**
 * The stored geometry was chosen against whatever viewport the user had at the
 * time. A smaller one — another monitor, a resized browser — would otherwise
 * restore the window partly or wholly off screen, with no way to drag it back.
 */
const fitToViewport = (
  geometry: FloatingWindowGeometry,
): FloatingWindowGeometry => ({
  width: clamp(geometry.width, 0, window.innerWidth),
  height: clamp(geometry.height, 0, window.innerHeight),
  x: clamp(geometry.x, 0, Math.max(0, window.innerWidth - MIN_VISIBLE_PX)),
  y: clamp(geometry.y, 0, Math.max(0, window.innerHeight - MIN_VISIBLE_PX)),
});

/**
 * Remembers where the Copilot window was left and how big it was. Closing the
 * window unmounts it, so its position and size live outside React.
 */
export const useCopilotWindowGeometry = (): {
  restoredGeometry: FloatingWindowGeometry | null;
  persistGeometry: (geometry: FloatingWindowGeometry) => void;
} => {
  const [stored, setStored] = useLocalStorage<FloatingWindowGeometry | null>(
    STORAGE_KEY,
    null,
  );
  // Frozen for the lifetime of the open window: re-fitting on every render
  // would move the window under a user who is still dragging it.
  const [restoredGeometry] = useState(() =>
    isGeometry(stored) ? fitToViewport(stored) : null,
  );

  return { restoredGeometry, persistGeometry: setStored };
};
